import React from 'react';
import { ChevronDown, ArrowLeft, Send, X } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppModal } from '../../../components/AppModal';
import { pickPreferredSendableInboxAccountId } from '../../../types/mailDispatch';

export type TemplateSignwellInboxAccount = {
  id: string;
  email: string;
};

type TemplateSignwellSendModalProps = {
  open: boolean;
  documentName: string;
  submitting: boolean;
  error: string | null;
  inboxAccounts: TemplateSignwellInboxAccount[];
  inboxAccountsLoading: boolean;
  inboxAccountsError: string | null;
  preferredInboxAccountId?: string | null;
  initialRecipientName?: string;
  initialRecipientEmail?: string;
  onClose: () => void;
  onSubmit: (payload: {
    inboxAccountId: string;
    name: string;
    email: string;
    subject: string;
    message: string;
  }) => Promise<void>;
};

export function TemplateSignwellSendModal({
  open,
  documentName,
  submitting,
  error,
  inboxAccounts,
  inboxAccountsLoading,
  inboxAccountsError,
  preferredInboxAccountId = null,
  initialRecipientName = '',
  initialRecipientEmail = '',
  onClose,
  onSubmit,
}: TemplateSignwellSendModalProps) {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [accountPickerOpen, setAccountPickerOpen] = React.useState(false);
  const accountPickerRef = React.useRef<HTMLDivElement>(null);
  const userPickedAccountRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) return;
    const recipientName = initialRecipientName.trim();
    setName(recipientName);
    setEmail(initialRecipientEmail.trim());
    setSubject(`Bitte unterschreiben: ${documentName}`);
    setMessage(
      recipientName
        ? `Hallo ${recipientName},\n\nbitte unterschreiben Sie das Dokument „${documentName}".\n\n{unterschrift_link}\n\nVielen Dank!`
        : `Hallo,\n\nbitte unterschreiben Sie das Dokument „${documentName}".\n\n{unterschrift_link}\n\nVielen Dank!`,
    );
    setAccountPickerOpen(false);
    setSelectedAccountId('');
    userPickedAccountRef.current = false;
  }, [open, documentName, initialRecipientName, initialRecipientEmail]);

  React.useEffect(() => {
    if (!open || inboxAccountsLoading) return;
    setSelectedAccountId((prev) => {
      if (userPickedAccountRef.current && prev && inboxAccounts.some((a) => a.id === prev)) return prev;
      return pickPreferredSendableInboxAccountId(
        inboxAccounts.map((a) => a.id),
        preferredInboxAccountId,
      );
    });
  }, [open, inboxAccounts, inboxAccountsLoading, preferredInboxAccountId]);

  React.useEffect(() => {
    if (!accountPickerOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (!accountPickerRef.current?.contains(event.target as Node)) {
        setAccountPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [accountPickerOpen]);

  const selectedAccount = inboxAccounts.find((a) => a.id === selectedAccountId) ?? null;
  const canSubmit =
    Boolean(selectedAccountId) &&
    Boolean(name.trim()) &&
    Boolean(email.trim()) &&
    inboxAccounts.length > 0 &&
    !inboxAccountsLoading;

  const handleSubmit = () => {
    void onSubmit({
      inboxAccountId: selectedAccountId,
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
    });
  };

  if (!open) return null;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      overlayClassName="z-[80]"
      panelClassName="md:max-w-3xl"
      disableClose={submitting}
      ariaLabelledBy="signwell-send-title"
    >
      <div
        className="flex flex-col w-full h-full min-h-0 md:bg-app-surface md:rounded-xl md:shadow-2xl md:max-w-3xl md:max-h-[90vh] md:border md:border-app-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-app-border flex items-center justify-between gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="md:hidden p-1.5 -ml-1 rounded-lg text-app-text-muted hover:bg-app-hover disabled:opacity-50"
            aria-label="Zurück"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <p id="signwell-send-title" className="text-sm font-semibold text-app-text flex-1">
            Zur Unterschrift senden
          </p>
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="hidden md:inline-flex items-center justify-center w-8 h-8 rounded-full border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text text-xs disabled:opacity-50"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pipedrive-blue text-white text-sm disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {submitting ? '…' : 'Senden'}
          </button>
        </div>

        {(error || inboxAccountsError) ? (
          <div className="mx-4 mt-2 p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
            {error || inboxAccountsError}
          </div>
        ) : null}

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="px-4 pt-3 pb-2 space-y-2 border-b border-app-border shrink-0">
            <div ref={accountPickerRef} className="relative">
              <label className="block text-xs font-medium text-app-text-muted mb-1">Von</label>
              {inboxAccountsLoading ? (
                <InlineLoadingState label="Postfächer werden geladen…" className="justify-start py-2" />
              ) : inboxAccounts.length <= 1 ? (
                <div className="w-full rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-sm text-app-text-muted">
                  {selectedAccount?.email || 'Kein Nylas-Postfach verbunden'}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => setAccountPickerOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-2 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text hover:bg-app-hover disabled:opacity-50"
                  >
                    <span className="truncate">{selectedAccount?.email || 'Postfach wählen'}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 text-app-text-subtle transition-transform ${accountPickerOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {accountPickerOpen ? (
                    <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-app-border bg-app-surface shadow-lg overflow-hidden">
                      {inboxAccounts.map((account) => (
                        <button
                          key={account.id}
                          type="button"
                          onClick={() => {
                            userPickedAccountRef.current = true;
                            setSelectedAccountId(account.id);
                            setAccountPickerOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2.5 text-sm hover:bg-app-hover ${
                            account.id === selectedAccountId ? 'bg-blue-50 text-pipedrive-blue font-medium' : 'text-app-text'
                          }`}
                        >
                          {account.email}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
              <p className="mt-1 text-xs text-app-text-subtle">
                Einladung und Bestätigung werden von Ihrem verbundenen Postfach per Nylas versendet.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-app-text-muted mb-1">Name des Unterzeichners</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                placeholder="Max Mustermann"
                className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-app-text-muted mb-1">An</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                placeholder="max@beispiel.de"
                className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-app-text-muted mb-1">Betreff</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={submitting}
                placeholder="Betreff"
                className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
              />
            </div>
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={submitting}
            placeholder="Nachricht verfassen …"
            className="app-scrollbar flex-1 min-h-[240px] md:min-h-[320px] overflow-y-auto overscroll-contain px-4 py-3 text-sm outline-none resize-none whitespace-pre-wrap"
          />
          <p className="px-4 pb-3 text-xs text-app-text-subtle shrink-0">
            An der Stelle von <span className="font-mono">{'{unterschrift_link}'}</span> wird der Link eingefügt.
          </p>
        </div>

        <div className="hidden md:flex justify-end gap-2 shrink-0 px-4 py-3 border-t border-app-border bg-app-surface">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={submitting || !canSubmit}
            onClick={handleSubmit}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Wird gesendet…' : 'Senden'}
          </button>
        </div>
      </div>
    </AppModal>
  );
}
